import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { listChannelConnections } from '../channels/channelsApi'
import type { ChannelConnection } from '../channels/types'
import { Button, Card, I, Pill } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import {
  createZohoOrderPolicy,
  getZohoOrderRoutingCapabilities,
  getZohoOrderRoutingObservedValues,
  listZohoMasterData,
  listZohoOrderPolicies,
  refreshZohoMasterData,
  updateZohoOrderPolicy,
} from '../inventory/inventoryApi'
import type {
  ChannelOrderStatus,
  ZohoMasterDataCategory,
  ZohoMasterDataItem,
  ZohoOrderLifecycleAction,
  ZohoOrderPolicy,
  ZohoOrderPolicyActionMappingRequest,
  ZohoOrderRoutingOutputKey,
  ZohoOrderRoutingPredicate,
  ZohoOrderRoutingPredicateField,
  ZohoOrderRoutingPredicateOperator,
  ZohoOrderRoutingSegment,
} from '../inventory/types'
import { getProduct, listProducts } from '../products/productsApi'
import type { ProductListItem } from '../products/types'
import { getZohoPolicyPreview } from '../orders/ordersApi'
import type { ZohoOrderFacts, ZohoPolicyPreview } from '../orders/types'

const STEPS = ['scope', 'lifecycle', 'segments', 'matrix', 'review'] as const
const FIELDS: ZohoOrderRoutingPredicateField[] = [
  'CHANNEL_PROVIDER',
  'CHANNEL_CONNECTION_ID',
  'ORDER_STATUS',
  'RAW_STATUS',
  'PAYMENT_STATUS',
  'PAYMENT_METHOD_CODE',
  'PAYMENT_METHOD_NAME',
  'PAYMENT_METHOD_TYPE',
  'PAYMENT_METHOD_EXTERNAL_ID',
  'SHIPPING_METHOD_EXTERNAL_ID',
  'SHIPPING_METHOD_CODE',
  'SHIPPING_COMPANY',
  'SHIPPING_METHOD',
  'FULFILLMENT_CHANNEL',
  'FULFILLMENT_STATUS',
  'LINE_ITEM_SKU',
]
const OPERATORS: ZohoOrderRoutingPredicateOperator[] = [
  'EQUALS',
  'ONE_OF',
  'PREFIX',
  'SUFFIX',
  'CONTAINS',
]
const OUTPUTS: ZohoOrderRoutingOutputKey[] = [
  'CUSTOMER',
  'WAREHOUSE',
  'SALES_PERSON',
  'PAYMENT_TERMS',
  'DELIVERY_METHOD',
  'REPORTING_TAG',
  'PAYMENT_FEE_ITEM',
  'SHIPPING_FEE_ITEM',
]
const ACTIONS: ZohoOrderLifecycleAction[] = [
  'CREATE_SALES_ORDER',
  'CONFIRM_SALES_ORDER',
  'CREATE_INVOICE',
  'VOID_SALES_ORDER',
  'CREATE_CREDIT_NOTE',
]
const STATUSES: ChannelOrderStatus[] = [
  'NEW',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'UNKNOWN',
]
const MASTER: ZohoMasterDataCategory[] = [
  'CUSTOMER',
  'WAREHOUSE',
  'SALES_PERSON',
  'PAYMENT_TERMS',
  'DELIVERY_METHOD',
  'REPORTING_TAG',
  'REPORTING_TAG_OPTION',
]
const OUTPUT_CATEGORY: Partial<
  Record<ZohoOrderRoutingOutputKey, ZohoMasterDataCategory>
> = {
  CUSTOMER: 'CUSTOMER',
  WAREHOUSE: 'WAREHOUSE',
  SALES_PERSON: 'SALES_PERSON',
  PAYMENT_TERMS: 'PAYMENT_TERMS',
  DELIVERY_METHOD: 'DELIVERY_METHOD',
  REPORTING_TAG: 'REPORTING_TAG',
}

type ActionDraft = {
  action: ZohoOrderLifecycleAction
  statuses: ChannelOrderStatus[]
  raw: string
}
const TYPED_FIELDS = new Set<ZohoOrderRoutingPredicateField>([
  'CHANNEL_PROVIDER',
  'CHANNEL_CONNECTION_ID',
  'ORDER_STATUS',
])
const emptyPredicate = (): ZohoOrderRoutingPredicate => ({
  field: 'CHANNEL_PROVIDER',
  operator: 'EQUALS',
  lineScope: null,
  values: [''],
})
const defaultSegment = (): ZohoOrderRoutingSegment => ({
  name: '',
  type: 'DEFAULT',
  predicates: [],
  cells: [],
})
const inputStyle = {
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box' as const,
  border: '1px solid var(--border-default)',
  borderRadius: 10,
  padding: '9px 10px',
  background: 'white',
  color: 'var(--fg)',
  fontFamily: 'inherit',
  fontSize: 13,
}

// Matrix cells carry the mode and the value in one control so a row can be read
// at a glance: an override reads as a solid chip, a value the Default supplies
// reads as a plain white chip, and an inherited cell stays muted text.
const INHERIT_OPTION = '\u0000inherit'
const CLEAR_OPTION = '\u0000clear'
const headerCellStyle = {
  padding: 10,
  textAlign: 'start' as const,
  fontSize: 10,
  lineHeight: '14px',
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: 'var(--ink-500)',
}
// Chips belong to the dense matrix table. The scope step is a roomy form, so
// there the same cell keeps an ordinary bordered control.
const cellControlStyle = (
  mode: 'INHERIT' | 'SET' | 'CLEAR',
  isDefault: boolean,
  dense: boolean,
) =>
  dense
    ? {
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box' as const,
        appearance: 'none' as const,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 11,
        lineHeight: '14px',
        padding: '6px 7px',
        borderRadius: 7,
        fontWeight: mode === 'SET' ? 700 : 400,
        color:
          mode !== 'SET'
            ? 'var(--ink-400)'
            : isDefault
              ? 'var(--ink-700)'
              : 'var(--brand-900)',
        background:
          mode !== 'SET'
            ? 'transparent'
            : isDefault
              ? 'white'
              : 'var(--brand-100)',
        border:
          mode === 'SET' && isDefault
            ? '1px solid var(--ink-200)'
            : '1px solid transparent',
      }
    : {
        ...inputStyle,
        fontWeight: mode === 'SET' ? 600 : 400,
        color: mode === 'SET' ? 'var(--fg)' : 'var(--fg-subtle)',
      }
// A fee item is either a resolved Zoho item or an unfilled slot the user must pick.
const feeChipStyle = (resolved: boolean, dense: boolean) => ({
  width: '100%',
  boxSizing: 'border-box' as const,
  marginBlockStart: dense ? 4 : 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'start' as const,
  fontSize: dense ? 10 : 12,
  lineHeight: dense ? '13px' : '16px',
  fontWeight: dense ? 800 : 700,
  padding: dense ? '6px 7px' : '9px 10px',
  borderRadius: dense ? 7 : 10,
  color: resolved ? 'var(--success-700)' : 'var(--warning-700)',
  background: resolved ? 'var(--success-100)' : 'var(--warning-100)',
  border: resolved ? '1px solid var(--success)' : '1px dashed var(--warning)',
})

// The matrix shows what a row matches, so a reader never has to go back to the
// segments step to recall the conditions behind a name.
function predicateSummary(
  segment: ZohoOrderRoutingSegment,
  t: (key: string) => string,
): string {
  return segment.predicates
    .map((predicate) => {
      const values = predicate.values.filter(Boolean).join(', ')
      const field = t(
        `pages.orderRouting.fields.${predicate.field}`,
      )
      return values ? `${field} ${values}` : field
    })
    .join(' · ')
}

function cleanSegment(
  segment: ZohoOrderRoutingSegment,
): ZohoOrderRoutingSegment {
  return {
    name: (segment.name ?? '').trim(),
    type: segment.type,
    predicates:
      segment.type === 'DEFAULT'
        ? []
        : segment.predicates.map((p) => ({
            field: p.field,
            operator: p.operator,
            lineScope:
              p.field === 'LINE_ITEM_SKU' ? (p.lineScope ?? 'ANY_LINE') : null,
            values: p.values.map((v) => v.trim()).filter(Boolean),
          })),
    cells: segment.cells.map((cell) => ({
      outputKey: cell.outputKey,
      valueMode: cell.valueMode,
      ...(cell.externalId ? { externalId: cell.externalId } : {}),
      ...(cell.secondaryExternalId
        ? { secondaryExternalId: cell.secondaryExternalId }
        : {}),
      ...(cell.productId ? { productId: cell.productId } : {}),
    })),
  }
}

export function OrderRoutingPage({
  accessToken,
  connectionId,
  policyId,
  sourceOrderId,
  onBack,
}: {
  accessToken: string
  connectionId: number | null
  policyId?: number | null
  sourceOrderId?: number | null
  onBack?: () => void
}) {
  const { t } = useTranslation()
  const mobile = useIsMobileViewport()
  const [step, setStep] = useState(0)
  const cards = mobile || step === 0
  const Matrix = cards ? 'div' : 'table'
  const MatrixBody = cards ? 'div' : 'tbody'
  const MatrixRow = cards ? 'div' : 'tr'
  const MatrixHeading = cards ? 'strong' : 'th'
  const MatrixCell = cards ? 'div' : 'td'
  const [loading, setLoading] = useState(connectionId != null)
  const [uiEnabled, setUiEnabled] = useState(false)
  const [observed, setObserved] = useState<
    Record<string, Array<{ value: string; orderCount: number }>>
  >({})
  const [observedError, setObservedError] = useState(false)
  const [observedRetry, setObservedRetry] = useState(0)
  const [selectedSegment, setSelectedSegment] = useState(0)
  const [saving, setSaving] = useState(false)
  const [refreshingMaster, setRefreshingMaster] = useState(false)
  const [refreshMasterFailed, setRefreshMasterFailed] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)
  const [policy, setPolicy] = useState<ZohoOrderPolicy | null>(null)
  const [copying, setCopying] = useState(false)
  const [invoiceSourceMode, setInvoiceSourceMode] = useState<
    'LOCAL_ORDER' | 'ZOHO_SALES_ORDER_SNAPSHOT'
  >('LOCAL_ORDER')
  const [previewOrderId, setPreviewOrderId] = useState(
    sourceOrderId?.toString() ?? '',
  )
  const [preview, setPreview] = useState<ZohoPolicyPreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(false)
  const [sourceFacts, setSourceFacts] = useState<ZohoOrderFacts | null>(null)
  const [selectedFacts, setSelectedFacts] = useState<string[]>([])
  const [sourceName, setSourceName] = useState('')
  const [name, setName] = useState('')
  const [channels, setChannels] = useState<ChannelConnection[]>([])
  const [channelIds, setChannelIds] = useState<number[]>([])
  const [actions, setActions] = useState<ActionDraft[]>(
    ACTIONS.map((action) => ({
      action,
      statuses: [],
      raw: '',
    })),
  )
  const [segments, setSegments] = useState<ZohoOrderRoutingSegment[]>([
    defaultSegment(),
  ])
  const [master, setMaster] = useState<Record<string, ZohoMasterDataItem[]>>({})
  const [products, setProducts] = useState<ProductListItem[]>([])
  const [productQuery, setProductQuery] = useState('')
  const [productsLoading, setProductsLoading] = useState(false)
  const [productsError, setProductsError] = useState(false)
  const [retryNonce, setRetryNonce] = useState(0)
  const previewRequest = useRef(0)
  const pickerDialog = useRef<HTMLDialogElement>(null)
  const [productPicker, setProductPicker] = useState<{
    segment: number
    output: ZohoOrderRoutingOutputKey
    productId: number | null
  } | null>(null)

  const [fetchedProduct, setFetchedProduct] = useState<{
    id: number
    name: string
    sku: string
  } | null>(null)

  useEffect(() => {
    if (productPicker) pickerDialog.current?.showModal()
    else pickerDialog.current?.close()
  }, [productPicker])

  // Fee cells hold a product id, and the search page rarely contains it. Resolve
  // the ids this policy actually uses so the matrix can name them instead of
  // printing a bare number.
  const [feeProducts, setFeeProducts] = useState<
    Record<number, { name: string; sku: string }>
  >({})
  const unresolvedFeeIds = useMemo(() => {
    const ids = new Set<number>()
    for (const segment of segments)
      for (const cell of segment.cells)
        if (
          cell.outputKey.endsWith('FEE_ITEM') &&
          cell.valueMode === 'SET' &&
          cell.productId != null &&
          !cell.resolvedLabel &&
          !products.some((product) => product.id === cell.productId) &&
          !(cell.productId in feeProducts)
        )
          ids.add(cell.productId)
    return [...ids].sort((a, b) => a - b)
  }, [segments, products, feeProducts])
  const unresolvedFeeKey = unresolvedFeeIds.join(',')
  useEffect(() => {
    if (!unresolvedFeeKey) return
    let cancelled = false
    void Promise.all(
      unresolvedFeeKey.split(',').map((raw: string) => {
        const id = Number(raw)
        return getProduct(accessToken, id)
          .then(
            (product) =>
              [id, { name: product.name, sku: product.sku }] as const,
          )
          .catch(() => null)
      }),
    ).then((entries) => {
      const resolved = entries.filter(Boolean) as [
        number,
        { name: string; sku: string },
      ][]
      if (!cancelled && resolved.length)
        setFeeProducts((current) => ({
          ...current,
          ...Object.fromEntries(resolved),
        }))
    })
    return () => {
      cancelled = true
    }
  }, [accessToken, unresolvedFeeKey])

  // The chosen product is often absent from the current search page, so fall
  // back to a lookup by id: reopening the picker must always show what is
  // already selected.
  const pickedProductId = productPicker?.productId ?? null
  const knownProduct =
    pickedProductId == null
      ? null
      : (products.find((product) => product.id === pickedProductId) ?? null)
  const pickerProduct =
    pickedProductId == null
      ? null
      : knownProduct
        ? {
            id: knownProduct.id,
            name: knownProduct.name,
            sku: knownProduct.sku,
          }
        : fetchedProduct?.id === pickedProductId
          ? fetchedProduct
          : null

  useEffect(() => {
    if (pickedProductId == null || knownProduct) return
    let cancelled = false
    getProduct(accessToken, pickedProductId)
      .then((product) => {
        if (!cancelled)
          setFetchedProduct({
            id: product.id,
            name: product.name,
            sku: product.sku,
          })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [accessToken, pickedProductId, knownProduct])

  function invalidateDraft() {
    setSaved(false)
    setPreview(null)
    setPreviewError(false)
    setPreviewLoading(false)
    previewRequest.current += 1
  }

  useEffect(() => {
    if (step !== 2 || !connectionId || !channelIds.length || !uiEnabled) return
    let active = true
    getZohoOrderRoutingObservedValues(
      accessToken,
      connectionId,
      channelIds,
      FIELDS.filter(
        (field) =>
          field !== 'LINE_ITEM_SKU' && field !== 'CHANNEL_CONNECTION_ID',
      ),
    )
      .then((result) => {
        if (active) {
          setObserved(
            Object.fromEntries(
              result.fields.map((field) => [field.field, field.values]),
            ),
          )
          setObservedError(false)
        }
      })
      .catch(() => {
        if (active) {
          setObserved({})
          setObservedError(true)
        }
      })
    return () => {
      active = false
    }
  }, [accessToken, connectionId, channelIds, step, uiEnabled, observedRetry])

  useEffect(() => {
    if (connectionId == null) return
    let cancelled = false
    Promise.all([
      listZohoOrderPolicies(accessToken, connectionId),
      listChannelConnections(accessToken),
      getZohoOrderRoutingCapabilities(accessToken, connectionId),
      ...MASTER.map((category) =>
        listZohoMasterData(accessToken, connectionId, category),
      ),
      listProducts(accessToken, { size: 25 }),
    ])
      .then(([policies, nextChannels, capabilities, ...rest]) => {
        if (cancelled) return
        setUiEnabled(capabilities.uiEnabled)
        const selected =
          policyId == null
            ? null
            : ((policies as ZohoOrderPolicy[]).find((p) => p.id === policyId) ??
              null)
        if (policyId != null && selected == null)
          throw new Error('Policy not found')
        setPolicy(selected)
        setChannels(nextChannels as ChannelConnection[])
        const nextMaster: Record<string, ZohoMasterDataItem[]> = {}
        MASTER.forEach((category, index) => {
          nextMaster[category] = rest[index] as ZohoMasterDataItem[]
        })
        setMaster(nextMaster)
        setProducts(
          (rest[MASTER.length] as unknown as { items: ProductListItem[] })
            .items ?? [],
        )
        if (selected) {
          setName(selected.name)
          setInvoiceSourceMode(selected.invoiceSourceMode ?? 'LOCAL_ORDER')
          setChannelIds(selected.channelConnectionIds ?? [])
          setActions([
            ...(selected.actionMappings ?? [])
              .sort((a, b) => a.priority - b.priority)
              .map((m) => ({
                action: m.action,
                statuses: [...new Set(m.statuses.map((s) => s.orderStatus))],
                raw: m.statuses
                  .map((s) => s.rawStatus)
                  .filter(Boolean)
                  .join(', '),
              })),
            ...ACTIONS.filter(
              (action) =>
                !selected.actionMappings.some((m) => m.action === action),
            ).map((action) => ({ action, statuses: [], raw: '' })),
          ])
          if (selected.routing?.segments.length)
            setSegments(selected.routing.segments.map(cleanSegment))
          setSaved(selected.status === 'DRAFT')
        }
      })
      .catch((caught) => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, connectionId, policyId, retryNonce])

  useEffect(() => {
    if (connectionId == null) return
    let active = true
    const timer = window.setTimeout(() => {
      setProductsLoading(true)
      setProductsError(false)
      listProducts(accessToken, { size: 25, query: productQuery })
        .then((page) => {
          if (active) setProducts(page.items ?? [])
        })
        .catch(() => {
          if (active) setProductsError(true)
        })
        .finally(() => {
          if (active) setProductsLoading(false)
        })
    }, 250)
    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [accessToken, connectionId, productQuery])

  useEffect(() => {
    if (sourceOrderId == null) return
    let cancelled = false
    getZohoPolicyPreview(accessToken, sourceOrderId)
      .then((result) => {
        if (!cancelled) setSourceFacts(result.facts)
      })
      .catch((caught) => {
        if (!cancelled) setError(caught)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, sourceOrderId])

  const locked = !copying && policy != null && policy.status !== 'DRAFT'
  const legacy = policy != null && policy.routingModelVersion !== 2
  // Default is checked last, so it cannot inherit from anything: without a customer
  // and a warehouse there is no order this policy can route. A draft may still be
  // saved incomplete — this gates activation, not saving.
  const defaultCells =
    segments.find((s) => s.type === 'DEFAULT')?.cells ?? []
  const defaultRouteComplete = (['CUSTOMER', 'WAREHOUSE'] as const).every(
    (output) =>
      defaultCells.some(
        (c) =>
          c.outputKey === output &&
          c.valueMode === 'SET' &&
          Boolean(c.externalId),
      ),
  )
  const valid =
    name.trim() &&
    channelIds.length > 0 &&
    actions.some((a) => a.statuses.length > 0) &&
    segments.filter((s) => s.type === 'DEFAULT').length === 1 &&
    segments
      .filter((s) => s.type === 'MATCH')
      .every(
        (s) =>
          (s.name ?? '').trim() &&
          s.predicates.length > 0 &&
          s.predicates.every((p) => p.values.some(Boolean)),
      ) &&
    segments.every((s) =>
      s.cells.every(
        (c) =>
          c.valueMode === 'CLEAR' ||
          (c.outputKey.endsWith('FEE_ITEM')
            ? Boolean(c.productId)
            : Boolean(c.externalId) &&
              (c.outputKey !== 'REPORTING_TAG' ||
                Boolean(c.secondaryExternalId))),
      ),
    )
  const errorText = String(
    error instanceof ApiClientError
      ? t(`pages.orderRouting.errors.${error.code}`, {
          defaultValue: t('pages.orderRouting.errors.REQUEST_FAILED'),
        })
      : t('pages.orderRouting.errors.REQUEST_FAILED'),
  )
  async function refreshMaster() {
    if (connectionId == null || refreshingMaster) return
    setRefreshingMaster(true)
    setRefreshMasterFailed(false)
    try {
      await refreshZohoMasterData(accessToken, connectionId, {
        categories: MASTER.filter((category) => category !== 'REPORTING_TAG_OPTION'),
      })
      const rows = await Promise.all(
        MASTER.map((category) => listZohoMasterData(accessToken, connectionId, category)),
      )
      setMaster(Object.fromEntries(MASTER.map((category, index) => [category, rows[index]])))
      invalidateDraft()
    } catch {
      setRefreshMasterFailed(true)
    } finally {
      setRefreshingMaster(false)
    }
  }

  function updateSegment(index: number, next: ZohoOrderRoutingSegment) {
    invalidateDraft()
    setSegments((current) =>
      current.map((segment, i) => (i === index ? next : segment)),
    )
  }
  function move(index: number, delta: number) {
    if (
      index + delta < 0 ||
      index + delta >= segments.filter((s) => s.type === 'MATCH').length
    )
      return
    setSelectedSegment(index + delta)
    invalidateDraft()
    setSegments((current) => {
      const matches = current.filter((s) => s.type === 'MATCH')
      const next = index + delta
      if (next < 0 || next >= matches.length) return current
      ;[matches[index], matches[next]] = [matches[next], matches[index]]
      return [...matches, ...current.filter((s) => s.type === 'DEFAULT')]
    })
  }
  function setCell(
    segmentIndex: number,
    outputKey: ZohoOrderRoutingOutputKey,
    mode: 'INHERIT' | 'SET' | 'CLEAR',
    value = '',
  ) {
    const segment = segments[segmentIndex]
    const cells = segment.cells.filter((cell) => cell.outputKey !== outputKey)
    if (mode !== 'INHERIT') {
      const fee = outputKey.endsWith('FEE_ITEM')
      const reporting = outputKey === 'REPORTING_TAG'
      const [externalId, secondaryExternalId] = reporting
        ? value.split('|')
        : [value, undefined]
      cells.push({
        outputKey,
        valueMode: mode,
        ...(mode === 'SET' && fee && value ? { productId: Number(value) } : {}),
        ...(mode === 'SET' && !fee && externalId ? { externalId } : {}),
        ...(mode === 'SET' && secondaryExternalId
          ? { secondaryExternalId }
          : {}),
      })
    }
    updateSegment(segmentIndex, { ...segment, cells })
  }

  async function convertLegacy() {
    if (!policy || !connectionId || saving) return
    setSaving(true)
    setError(null)
    const payload = {
      name:
        policy.status === 'DRAFT'
          ? policy.name
          : t('pages.inventoryZoho.rules.copyName', { name: policy.name }),
      channelConnectionIds: policy.channelConnectionIds,
      invoiceSourceMode: policy.invoiceSourceMode ?? 'LOCAL_ORDER',
      routingModelVersion: 2 as const,
      legacyRoutingSourcePolicyId: policy.id,
      actionMappings: policy.actionMappings.map((mapping) => ({
        priority: mapping.priority,
        action: mapping.action,
        orderStatuses: [
          ...new Set(mapping.statuses.map((status) => status.orderStatus)),
        ],
        rawStatuses: [
          ...new Set(
            mapping.statuses
              .map((status) => status.rawStatus)
              .filter((value): value is string => Boolean(value)),
          ),
        ],
      })),
    }
    try {
      const result =
        policy.status === 'DRAFT'
          ? await updateZohoOrderPolicy(
              accessToken,
              connectionId,
              policy.id,
              payload,
            )
          : await createZohoOrderPolicy(accessToken, connectionId, payload)
      setPolicy(result)
      setName(result.name)
      setSaved(true)
      setSegments(
        result.routing?.segments.map(cleanSegment) ?? [defaultSegment()],
      )
    } catch (caught) {
      setError(caught)
    } finally {
      setSaving(false)
    }
  }

  async function save() {
    if (!connectionId || !valid || locked || legacy) return
    setSaving(true)
    setError(null)
    invalidateDraft()
    const actionMappings: ZohoOrderPolicyActionMappingRequest[] = actions
      .filter((a) => a.statuses.length)
      .map((a, i) => ({
        priority: (i + 1) * 10,
        action: a.action,
        orderStatuses: a.statuses,
        rawStatuses: a.raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean),
      }))
    const payload = {
      name: name.trim(),
      channelConnectionIds: channelIds,
      invoiceSourceMode,
      actionMappings,
      routingModelVersion: 2 as const,
      routing: { segments: segments.map(cleanSegment) },
    }
    try {
      const result =
        !copying && policy?.status === 'DRAFT'
          ? await updateZohoOrderPolicy(
              accessToken,
              connectionId,
              policy.id,
              payload,
            )
          : await createZohoOrderPolicy(accessToken, connectionId, payload)
      setPolicy(result)
      setCopying(false)
      setSaved(true)
    } catch (caught) {
      setError(caught)
    } finally {
      setSaving(false)
    }
  }

  async function loadPreview() {
    if (
      !policy ||
      !saved ||
      !Number.isSafeInteger(Number(previewOrderId)) ||
      Number(previewOrderId) <= 0
    )
      return
    const request = ++previewRequest.current
    setPreviewLoading(true)
    setPreviewError(false)
    try {
      const result = await getZohoPolicyPreview(
        accessToken,
        Number(previewOrderId),
        policy.id,
      )
      if (request === previewRequest.current) setPreview(result)
    } catch {
      if (request === previewRequest.current) setPreviewError(true)
    } finally {
      if (request === previewRequest.current) setPreviewLoading(false)
    }
  }

  const facts = sourceFacts
    ? Object.entries({
        CHANNEL_PROVIDER: sourceFacts.provider,
        CHANNEL_CONNECTION_ID: sourceFacts.channelConnectionId?.toString(),
        ORDER_STATUS: sourceFacts.status,
        RAW_STATUS: sourceFacts.rawStatus,
        PAYMENT_STATUS: sourceFacts.paymentStatus,
        PAYMENT_METHOD_CODE: sourceFacts.paymentMethodCode,
        PAYMENT_METHOD_NAME: sourceFacts.paymentMethodName,
        PAYMENT_METHOD_TYPE: sourceFacts.paymentMethodType,
        PAYMENT_METHOD_EXTERNAL_ID: sourceFacts.paymentMethodExternalId,
        SHIPPING_METHOD_EXTERNAL_ID: sourceFacts.shippingMethodExternalId,
        SHIPPING_METHOD_CODE: sourceFacts.shippingMethodCode,
        SHIPPING_COMPANY: sourceFacts.shippingCompany,
        SHIPPING_METHOD: sourceFacts.shippingMethod,
        FULFILLMENT_CHANNEL: sourceFacts.fulfillmentChannel,
        FULFILLMENT_STATUS: sourceFacts.fulfillmentStatus,
      }).filter(
        (entry): entry is [string, string] =>
          typeof entry[1] === 'string' && entry[1].trim().length > 0,
      )
    : []

  function addFromOrder() {
    if (!sourceName.trim() || !selectedFacts.length || locked) return
    const predicates = facts
      .filter(([field]) => selectedFacts.includes(field))
      .map(([field, value]) => ({
        field: field as ZohoOrderRoutingPredicateField,
        operator: 'EQUALS' as const,
        values: [value],
      }))
    if (!predicates.length) return
    setSelectedSegment(segments.length - 1)
    setSegments((current) => [
      ...current.filter((s) => s.type === 'MATCH'),
      { name: sourceName.trim(), type: 'MATCH', predicates, cells: [] },
      ...current.filter((s) => s.type === 'DEFAULT'),
    ])
    invalidateDraft()
    setSelectedFacts([])
    setSourceName('')
  }

  if (connectionId == null)
    return (
      <Card padding={24}>
        <p>{t('pages.orderRouting.noConnection')}</p>
        <Button kind="secondary" onClick={onBack}>
          {t('pages.orderRouting.back')}
        </Button>
      </Card>
    )
  if (loading)
    return (
      <Card padding={24}>
        <p>{t('pages.orderRouting.loading')}</p>
      </Card>
    )
  if (error && !policy && !name)
    return (
      <Card padding={24}>
        <p style={{ color: 'var(--danger)' }}>{errorText}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            kind="secondary"
            onClick={() => {
              setError(null)
              setLoading(true)
              setRetryNonce((n) => n + 1)
            }}
          >
            {t('pages.orderRouting.retry')}
          </Button>
          <Button kind="ghost" onClick={onBack}>
            {t('pages.orderRouting.back')}
          </Button>
        </div>
      </Card>
    )
  if (!uiEnabled)
    return (
      <Card>
        <p>{t('pages.orderRouting.disabled')}</p>
        <Button kind="secondary" onClick={onBack}>
          {t('pages.orderRouting.back')}
        </Button>
      </Card>
    )
  if (legacy)
    return (
      <Card padding={24}>
        <Pill tone="warning">
          {t('pages.orderRouting.compatibility.badge')}
        </Pill>
        <h2>{t('pages.orderRouting.compatibility.title')}</h2>
        <p style={{ color: 'var(--fg-muted)' }}>
          {policy?.routing?.segments.length
            ? t('pages.orderRouting.compatibility.available')
            : t('pages.orderRouting.compatibility.unavailable')}
        </p>
        {Boolean(policy?.routing?.dormantExceptionRuleCount) && (
          <p>
            {t('pages.orderRouting.compatibility.dormant', {
              count: policy?.routing?.dormantExceptionRuleCount,
            })}
          </p>
        )}
        {Boolean(error) && (
          <p role="alert" style={{ color: 'var(--danger)' }}>
            {errorText}
          </p>
        )}
        <Button
          kind="primary"
          disabled={
            saving ||
            !policy?.routing?.segments.length ||
            Boolean(policy.routing.compatibilityBlockers?.length)
          }
          onClick={convertLegacy}
        >
          {t(
            policy?.status === 'DRAFT'
              ? 'pages.orderRouting.compatibility.convert'
              : 'pages.orderRouting.copy',
          )}
        </Button>
        <Button kind="secondary" onClick={onBack}>
          {t('pages.orderRouting.back')}
        </Button>
      </Card>
    )

  return (
    <fieldset
      disabled={saving}
      style={{
        border: 0,
        padding: 0,
        margin: 0,
        maxWidth: 1180,
        width: '100%',
        minWidth: 0,
        paddingBlockEnd: 'var(--sp-24)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBlockEnd: 18,
        }}
      >
        <div>
          <button
            onClick={onBack}
            style={{
              border: 0,
              background: 'transparent',
              padding: 0,
              color: 'var(--fg-muted)',
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            {t('pages.orderRouting.back')}
          </button>
          <h2
            style={{
              margin: '6px 0 0',
              color: 'var(--brand-900)',
              fontSize: 'var(--fs-30)',
              lineHeight: 'var(--lh-snug)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              flexWrap: 'wrap',
            }}
          >
            {name || t('pages.orderRouting.title')}
            {policy && (
              <Pill
                tone={
                  policy.status === 'DRAFT' || copying ? 'warning' : 'neutral'
                }
              >
                {t(
                  `pages.orderRouting.policyStatus.${copying ? 'DRAFT' : policy.status}`,
                )}{' '}
                · {policy.version}
              </Pill>
            )}
          </h2>
          {sourceOrderId && (
            <p
              style={{
                margin: '3px 0 0',
                color: 'var(--fg-muted)',
                fontSize: 12,
              }}
            >
              {t('pages.orderRouting.sourceOrder', { id: sourceOrderId })}
            </p>
          )}
        </div>
        {!locked && (
          <div
            style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}
          >
            <Button kind="secondary" icon={I.refresh} disabled={refreshingMaster} onClick={refreshMaster}>
              {t(refreshingMaster
                ? 'pages.syncRuleWizard.actions.refreshingMasterData'
                : 'pages.syncRuleWizard.actions.refreshMasterData')}
            </Button>
            <Button kind="secondary" disabled={!valid || saving} onClick={save}>
              {t('pages.orderRouting.review.save')}
            </Button>
            {step < 4 && (
              <Button kind="primary" onClick={() => setStep((s) => s + 1)}>
                {t('pages.orderRouting.next')} ·{' '}
                {t(`pages.orderRouting.steps.${STEPS[step + 1]}`)}
              </Button>
            )}
          </div>
        )}
        {locked && <Pill tone="neutral">{t('pages.orderRouting.locked')}</Pill>}
        {locked && (
          <Button
            kind="secondary"
            onClick={() => {
              setCopying(true)
              setName(
                t('pages.inventoryZoho.rules.copyName', { name: policy?.name }),
              )
              setSaved(false)
              setPreview(null)
            }}
          >
            {t('pages.orderRouting.copy')}
          </Button>
        )}
      </div>
      {refreshMasterFailed && (
        <p role="alert" style={{ color: 'var(--danger)' }}>
          {t('pages.syncRuleWizard.errors.codes.ZOHO_MASTER_DATA_REFRESH_FAILED')}
        </p>
      )}
      <div
        aria-label={t('pages.orderRouting.steps.label')}
        style={{
          display: 'flex',
          gap: 6,
          overflowX: 'auto',
          marginBlockEnd: 'var(--sp-6)',
          padding: 'var(--sp-1)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-card)',
        }}
      >
        {STEPS.map((s, i) => (
          <button
            key={s}
            aria-current={i === step ? 'step' : undefined}
            onClick={() => setStep(i)}
            style={{
              border: 0,
              flex: mobile ? undefined : 1,
              borderRadius: 'var(--r-sm)',
              padding: 'var(--sp-2) var(--sp-3)',
              textAlign: 'start',
              cursor: 'pointer',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              background: i === step ? 'var(--brand-100)' : 'transparent',
              color: i === step ? 'var(--brand-700)' : 'var(--fg-subtle)',
              fontFamily: 'inherit',
            }}
          >
            {i + 1}. {t(`pages.orderRouting.steps.${s}`)}
          </button>
        ))}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            step === 0 && !mobile
              ? 'minmax(240px,1fr) minmax(0,2fr)'
              : 'minmax(0,1fr)',
          gap: 'var(--sp-4)',
          alignItems: 'start',
        }}
      >
        {step === 0 && (
          <Card padding={mobile ? 16 : 24} style={{ minWidth: 0 }}>
            <h3>{t('pages.orderRouting.scope.title')}</h3>
            <label>
              {t('pages.orderRouting.scope.name')}
              <input
                style={inputStyle}
                value={name}
                disabled={locked}
                onChange={(e) => {
                  invalidateDraft()
                  setName(e.target.value)
                }}
              />
            </label>
            <label style={{ display: 'block', marginBlockStart: 14 }}>
              {t('pages.orderRouting.scope.invoiceSource')}
              <select
                style={inputStyle}
                value={invoiceSourceMode}
                disabled={locked}
                onChange={(e) => {
                  invalidateDraft()
                  setInvoiceSourceMode(
                    e.target.value as typeof invoiceSourceMode,
                  )
                }}
              >
                <option value="LOCAL_ORDER">
                  {t('pages.orderRouting.scope.localOrder')}
                </option>
                <option value="ZOHO_SALES_ORDER_SNAPSHOT">
                  {t('pages.orderRouting.scope.zohoSnapshot')}
                </option>
              </select>
            </label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr)',
                gap: 10,
                marginBlockStart: 16,
              }}
            >
              {channels.map((c) => (
                <label
                  key={c.id}
                  style={{
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 10,
                    padding: 12,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={channelIds.includes(c.id)}
                    disabled={locked}
                    onChange={() => {
                      invalidateDraft()
                      setChannelIds((ids) =>
                        ids.includes(c.id)
                          ? ids.filter((id) => id !== c.id)
                          : [...ids, c.id],
                      )
                    }}
                  />{' '}
                  {c.displayName} · {c.provider}
                </label>
              ))}
            </div>
          </Card>
        )}
        {step === 1 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: mobile
                ? 'minmax(0,1fr)'
                : '280px minmax(0,1fr)',
              gap: 'var(--sp-4)',
            }}
          >
            <Card padding={20}>
              <h3 style={{ color: 'var(--brand-900)', marginBlockStart: 0 }}>
                {t('pages.orderRouting.lifecycle.title')}
              </h3>
              <p style={{ color: 'var(--fg-muted)' }}>
                {t('pages.orderRouting.design.lifecycleHelp')}
              </p>
              <p
                style={{
                  padding: 'var(--sp-4)',
                  borderRadius: 'var(--r-md)',
                  background: 'var(--ink-900)',
                  color: 'var(--white)',
                }}
              >
                {t('pages.orderRouting.design.lifecycleNotice')}
              </p>
            </Card>
            <Card padding={mobile ? 16 : 24} style={{ minWidth: 0 }}>
              <h3>{t('pages.orderRouting.lifecycle.title')}</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                {actions.map((a, i) => (
                  <div
                    key={a.action}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: mobile ? '1fr' : '210px 1fr',
                      gap: 10,
                      borderBlockEnd: '1px solid var(--border-subtle)',
                      paddingBlock: 10,
                    }}
                  >
                    <div
                      style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                    >
                      <strong style={{ flex: 1 }}>
                        {t(`pages.orderRouting.actions.${a.action}`)}
                      </strong>
                      <Button
                        kind="secondary"
                        size="xs"
                        ariaLabel={t('pages.orderRouting.lifecycle.moveUp')}
                        disabled={locked || i === 0}
                        onClick={() => {
                          invalidateDraft()
                          setActions((current) => {
                            const next = [...current]
                            ;[next[i - 1], next[i]] = [next[i], next[i - 1]]
                            return next
                          })
                        }}
                      >
                        ↑
                      </Button>
                      <Button
                        kind="secondary"
                        size="xs"
                        ariaLabel={t('pages.orderRouting.lifecycle.moveDown')}
                        disabled={locked || i === actions.length - 1}
                        onClick={() => {
                          invalidateDraft()
                          setActions((current) => {
                            const next = [...current]
                            ;[next[i + 1], next[i]] = [next[i], next[i + 1]]
                            return next
                          })
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                    <div>
                      {STATUSES.map((status) => (
                        <label
                          key={status}
                          style={{
                            display: 'inline-flex',
                            gap: 4,
                            marginInlineEnd: 12,
                          }}
                        >
                          <input
                            type="checkbox"
                            disabled={locked}
                            checked={a.statuses.includes(status)}
                            onChange={() => {
                              invalidateDraft()
                              setActions((current) =>
                                current.map((x, j) =>
                                  j === i
                                    ? {
                                        ...x,
                                        statuses: x.statuses.includes(status)
                                          ? x.statuses.filter(
                                              (s) => s !== status,
                                            )
                                          : [...x.statuses, status],
                                      }
                                    : x,
                                ),
                              )
                            }}
                          />
                          {t(`pages.orderRouting.statuses.${status}`)}
                        </label>
                      ))}
                      <input
                        style={{ ...inputStyle, marginBlockStart: 8 }}
                        disabled={locked}
                        placeholder={t('pages.orderRouting.lifecycle.raw')}
                        value={a.raw}
                        onChange={(e) => {
                          invalidateDraft()
                          setActions((current) =>
                            current.map((x, j) =>
                              j === i ? { ...x, raw: e.target.value } : x,
                            ),
                          )
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
        {step === 2 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: mobile
                ? 'minmax(0,1fr)'
                : 'minmax(240px, 1fr) minmax(0,2fr)',
              gap: 'var(--sp-4)',
              alignItems: 'start',
            }}
          >
            <Card padding={16} style={{ minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                }}
              >
                <h3 style={{ color: 'var(--brand-900)', margin: 0 }}>
                  {t('pages.orderRouting.segments.title')}
                </h3>
                <Button
                  size="sm"
                  icon={I.plus}
                  disabled={locked}
                  onClick={() => {
                    invalidateDraft()
                    setSelectedSegment(segments.length - 1)
                    setSegments((current) => [
                      ...current.filter((s) => s.type === 'MATCH'),
                      {
                        name: '',
                        type: 'MATCH',
                        predicates: [emptyPredicate()],
                        cells: [],
                      },
                      ...current.filter((s) => s.type === 'DEFAULT'),
                    ])
                  }}
                >
                  {t('pages.orderRouting.segments.add')}
                </Button>
              </div>
              <div
                style={{
                  display: 'grid',
                  gap: 'var(--sp-3)',
                  marginBlockStart: 'var(--sp-4)',
                }}
              >
                {segments.map((segment, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedSegment(index)}
                    aria-pressed={selectedSegment === index}
                    style={{
                      width: '100%',
                      minWidth: 0,
                      textAlign: 'start',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      padding: 'var(--sp-3)',
                      borderRadius: 'var(--r-md)',
                      border: `1px solid var(${selectedSegment === index ? '--brand-300' : '--border-subtle'})`,
                      background:
                        selectedSegment === index
                          ? 'var(--brand-100)'
                          : segment.type === 'DEFAULT'
                            ? 'var(--ink-100)'
                            : 'var(--bg-card)',
                      color: 'var(--brand-900)',
                    }}
                  >
                    <strong
                      style={{ display: 'block', overflowWrap: 'anywhere' }}
                    >
                      {segment.type === 'DEFAULT'
                        ? t('pages.orderRouting.segments.default')
                        : segment.name || t('pages.orderRouting.segments.add')}
                    </strong>
                    {segment.type === 'DEFAULT' && (
                      <Pill size="sm">
                        {t('pages.orderRouting.segments.pinned')}
                      </Pill>
                    )}
                    {selectedSegment === index && (
                      <Pill tone="brand" size="sm">
                        {t('pages.orderRouting.segments.selected')}
                      </Pill>
                    )}
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--fs-12)',
                        color: 'var(--fg-subtle)',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {segment.predicates
                        .map(
                          (p) =>
                            `${t(`pages.orderRouting.fields.${p.field}`)} · ${p.values.join(', ')}`,
                        )
                        .join(' / ')}
                    </span>
                  </button>
                ))}
              </div>
            </Card>
            <Card padding={mobile ? 16 : 24} style={{ minWidth: 0 }}>
              <h3 style={{ marginBlockStart: 0, color: 'var(--brand-900)' }}>
                {t('pages.orderRouting.segments.editor')}
              </h3>
              <p
                style={{ color: 'var(--fg-subtle)', fontSize: 'var(--fs-13)' }}
              >
                {t('pages.orderRouting.segments.editorHelp')}
              </p>
              {sourceFacts && (
                <section
                  aria-label={t('pages.orderRouting.source.title')}
                  style={{
                    padding: 14,
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 12,
                    marginBlockEnd: 16,
                  }}
                >
                  <h3 style={{ marginBlockStart: 0 }}>
                    {t('pages.orderRouting.source.title')}
                  </h3>
                  <p style={{ color: 'var(--fg-muted)' }}>
                    {t('pages.orderRouting.source.help')}
                  </p>
                  <label>
                    {t('pages.orderRouting.source.name')}
                    <input
                      style={inputStyle}
                      value={sourceName}
                      onChange={(e) => setSourceName(e.target.value)}
                    />
                  </label>
                  <div
                    style={{
                      display: 'grid',
                      gap: 'var(--sp-2)',
                      marginBlock: 'var(--sp-3)',
                    }}
                  >
                    {facts.map(([field, value]) => (
                      <label
                        key={field}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-2)',
                          overflowWrap: 'anywhere',
                          padding: 'var(--sp-3)',
                          borderRadius: 'var(--r-md)',
                          border: `1px solid var(${selectedFacts.includes(field) ? '--brand-300' : '--border-subtle'})`,
                          background: selectedFacts.includes(field)
                            ? 'var(--brand-100)'
                            : 'var(--bg-card)',
                        }}
                      >
                        <input
                          type="checkbox"
                          disabled={locked}
                          checked={selectedFacts.includes(field)}
                          onChange={() =>
                            setSelectedFacts((current) =>
                              current.includes(field)
                                ? current.filter((x) => x !== field)
                                : [...current, field],
                            )
                          }
                        />{' '}
                        {t(`pages.orderRouting.fields.${field}`)}:{' '}
                        <code>{value}</code>
                      </label>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    kind="secondary"
                    disabled={
                      locked || !sourceName.trim() || !selectedFacts.length
                    }
                    onClick={addFromOrder}
                  >
                    {t('pages.orderRouting.source.add')}
                  </Button>
                </section>
              )}
              {observedError && (
                <div
                  role="alert"
                  style={{
                    padding: 'var(--sp-3)',
                    background: 'var(--warning-100)',
                    borderRadius: 'var(--r-sm)',
                    marginBlockEnd: 'var(--sp-3)',
                  }}
                >
                  {t('pages.orderRouting.observed.error')}
                  <Button
                    kind="ghost"
                    size="sm"
                    onClick={() => setObservedRetry((n) => n + 1)}
                  >
                    {t('pages.orderRouting.retry')}
                  </Button>
                </div>
              )}
              <div style={{ display: 'grid', gap: 12 }}>
                {segments.map(
                  (segment, si) =>
                    si === selectedSegment && (
                      <div
                        key={si}
                        style={{
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 12,
                          padding: 14,
                          background:
                            segment.type === 'DEFAULT'
                              ? 'var(--ink-50)'
                              : 'white',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                          }}
                        >
                          <input
                            style={inputStyle}
                            disabled={locked || segment.type === 'DEFAULT'}
                            value={
                              segment.type === 'DEFAULT'
                                ? t('pages.orderRouting.segments.default')
                                : segment.name
                            }
                            onChange={(e) =>
                              updateSegment(si, {
                                ...segment,
                                name: e.target.value,
                              })
                            }
                          />
                          {segment.type === 'MATCH' && (
                            <>
                              <Button
                                kind="secondary"
                                size="sm"
                                disabled={locked || si === 0}
                                onClick={() => move(si, -1)}
                                ariaLabel={t(
                                  'pages.orderRouting.lifecycle.moveUp',
                                )}
                              >
                                ↑
                              </Button>
                              <Button
                                kind="secondary"
                                size="sm"
                                disabled={locked || si >= segments.length - 2}
                                onClick={() => move(si, 1)}
                                ariaLabel={t(
                                  'pages.orderRouting.lifecycle.moveDown',
                                )}
                              >
                                ↓
                              </Button>
                              <Button
                                kind="ghost"
                                size="sm"
                                disabled={locked}
                                onClick={() => {
                                  invalidateDraft()
                                  setSelectedSegment(Math.max(0, si - 1))
                                  setSegments((current) =>
                                    current.filter((_, i) => i !== si),
                                  )
                                }}
                              >
                                ×
                              </Button>
                            </>
                          )}
                        </div>
                        {segment.predicates.map((p, pi) => (
                          <div
                            key={pi}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: mobile
                                ? '1fr'
                                : 'minmax(0,1fr) minmax(0,1fr)',
                              gap: 8,
                              marginBlockStart: 10,
                            }}
                          >
                            <select
                              style={inputStyle}
                              value={p.field}
                              disabled={locked}
                              onChange={(e) => {
                                const field = e.target
                                  .value as ZohoOrderRoutingPredicateField
                                const operator =
                                  TYPED_FIELDS.has(field) &&
                                  !['EQUALS', 'ONE_OF'].includes(p.operator)
                                    ? 'EQUALS'
                                    : p.operator
                                const predicates = segment.predicates.map(
                                  (x, i) =>
                                    i === pi
                                      ? {
                                          ...x,
                                          field,
                                          operator,
                                          values: [],
                                          lineScope:
                                            field === 'LINE_ITEM_SKU'
                                              ? ('ANY_LINE' as const)
                                              : null,
                                        }
                                      : x,
                                )
                                updateSegment(si, { ...segment, predicates })
                              }}
                            >
                              {FIELDS.map((f) => (
                                <option key={f} value={f}>
                                  {t(`pages.orderRouting.fields.${f}`)}
                                </option>
                              ))}
                            </select>
                            <select
                              style={inputStyle}
                              value={p.operator}
                              disabled={locked}
                              onChange={(e) => {
                                const predicates = segment.predicates.map(
                                  (x, i) =>
                                    i === pi
                                      ? {
                                          ...x,
                                          operator: e.target
                                            .value as ZohoOrderRoutingPredicateOperator,
                                        }
                                      : x,
                                )
                                updateSegment(si, { ...segment, predicates })
                              }}
                            >
                              {OPERATORS.filter(
                                (o) =>
                                  !TYPED_FIELDS.has(p.field) ||
                                  o === 'EQUALS' ||
                                  o === 'ONE_OF',
                              ).map((o) => (
                                <option key={o} value={o}>
                                  {t(`pages.orderRouting.operators.${o}`)}
                                </option>
                              ))}
                            </select>
                            <input
                              style={inputStyle}
                              disabled={locked}
                              value={p.values.join(', ')}
                              list={`routing-values-${si}-${pi}`}
                              placeholder={t(
                                'pages.orderRouting.segments.values',
                              )}
                              onChange={(e) => {
                                const predicates = segment.predicates.map(
                                  (x, i) =>
                                    i === pi
                                      ? {
                                          ...x,
                                          values: e.target.value.split(','),
                                        }
                                      : x,
                                )
                                updateSegment(si, { ...segment, predicates })
                              }}
                            />
                            <datalist id={`routing-values-${si}-${pi}`}>
                              {(observed[p.field] ?? []).map((value) => (
                                <option key={value.value} value={value.value}>
                                  {t('pages.orderRouting.observed.count', {
                                    count: value.orderCount,
                                  })}
                                </option>
                              ))}
                            </datalist>
                            {!observedError &&
                              p.field !== 'LINE_ITEM_SKU' &&
                              p.field !== 'CHANNEL_CONNECTION_ID' &&
                              !observed[p.field]?.length && (
                                <small style={{ color: 'var(--fg-muted)' }}>
                                  {t(
                                    observedError
                                      ? 'pages.orderRouting.observed.error'
                                      : 'pages.orderRouting.observed.empty',
                                  )}
                                </small>
                              )}
                            {p.field === 'LINE_ITEM_SKU' && (
                              <select
                                style={inputStyle}
                                disabled={locked}
                                value={p.lineScope ?? 'ANY_LINE'}
                                onChange={(e) => {
                                  const predicates = segment.predicates.map(
                                    (x, i) =>
                                      i === pi
                                        ? {
                                            ...x,
                                            lineScope: e.target
                                              .value as 'ANY_LINE',
                                          }
                                        : x,
                                  )
                                  updateSegment(si, { ...segment, predicates })
                                }}
                              >
                                <option value="ANY_LINE">
                                  {t('pages.orderRouting.lineScope.ANY_LINE')}
                                </option>
                                <option value="ALL_LINES">
                                  {t('pages.orderRouting.lineScope.ALL_LINES')}
                                </option>
                                <option value="NO_LINE">
                                  {t('pages.orderRouting.lineScope.NO_LINE')}
                                </option>
                              </select>
                            )}
                            <Button
                              kind="ghost"
                              size="sm"
                              disabled={locked}
                              onClick={() =>
                                updateSegment(si, {
                                  ...segment,
                                  predicates: segment.predicates.filter(
                                    (_, i) => i !== pi,
                                  ),
                                })
                              }
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                        {segment.type === 'MATCH' && (
                          <Button
                            kind="ghost"
                            size="sm"
                            disabled={locked}
                            onClick={() =>
                              updateSegment(si, {
                                ...segment,
                                predicates: [
                                  ...segment.predicates,
                                  emptyPredicate(),
                                ],
                              })
                            }
                          >
                            {t('pages.orderRouting.segments.addPredicate')}
                          </Button>
                        )}
                      </div>
                    ),
                )}
              </div>
              <div
                style={{
                  padding: 'var(--sp-4)',
                  marginBlockStart: 'var(--sp-4)',
                  background: 'var(--brand-900)',
                  color: 'var(--ink-50)',
                  borderRadius: 'var(--r-md)',
                  fontSize: 'var(--fs-13)',
                }}
              >
                {t('pages.orderRouting.segments.matchHelp')}
              </div>
            </Card>
          </div>
        )}
        {(step === 0 || step === 3) && (
          <Card padding={mobile ? 12 : 20} style={{ minWidth: 0 }}>
            <h3 style={{ color: 'var(--brand-900)', marginBlockStart: 0 }}>
              {t(
                step === 0
                  ? 'pages.orderRouting.design.defaults'
                  : 'pages.orderRouting.matrix.title',
              )}
            </h3>
            <p style={{ color: 'var(--fg-subtle)', fontSize: 'var(--fs-13)' }}>
              {t('pages.orderRouting.design.inheritHelp')}
            </p>
            {/* Narrows which fee products the cells can name; it is a filter on
                the table below, not a field, so it stays small and out of the way. */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
                gap: 'var(--sp-2)',
                marginBlockEnd: 12,
                fontSize: 'var(--fs-12)',
                color: 'var(--fg-subtle)',
              }}
            >
              {t('pages.orderRouting.matrix.productSearch')}
              <input
                aria-label={t('pages.orderRouting.matrix.productSearch')}
                style={{
                  ...inputStyle,
                  width: mobile ? '100%' : 220,
                  padding: '6px 9px',
                  fontSize: 12,
                }}
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
              />
              {productsLoading && (
                <small>{t('pages.orderRouting.matrix.productsLoading')}</small>
              )}
              {productsError && (
                <small style={{ color: 'var(--danger)' }}>
                  {t('pages.orderRouting.matrix.productsError')}
                </small>
              )}
            </label>
            <div
              style={{
                overflowX: cards ? undefined : 'auto',
                border: cards ? undefined : '1px solid var(--ink-200)',
                borderRadius: cards ? undefined : 12,
                background: cards ? undefined : 'white',
              }}
            >
              <Matrix
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  tableLayout: 'fixed',
                }}
              >
                {!cards && (
                  <thead>
                    <tr
                      style={{
                        background: 'var(--ink-50)',
                        borderBlockEnd: '1px solid var(--ink-200)',
                        overflowWrap: 'anywhere',
                        textAlign: 'start',
                      }}
                    >
                      <th scope="col" style={{ ...headerCellStyle, width: 180 }}>
                        {t('pages.orderRouting.matrix.segment')}
                      </th>
                      {OUTPUTS.map((output) => (
                        <th
                          scope="col"
                          key={output}
                          style={{
                            ...headerCellStyle,
                            color: output.endsWith('FEE_ITEM')
                              ? 'var(--brand-700)'
                              : 'var(--ink-500)',
                            borderInlineStart:
                              output === 'PAYMENT_FEE_ITEM'
                                ? '1px solid var(--ink-200)'
                                : undefined,
                          }}
                        >
                          {t(`pages.orderRouting.outputs.${output}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <MatrixBody>
                  {segments.map(
                    (segment, si) =>
                      (step !== 0 || segment.type === 'DEFAULT') && (
                        <MatrixRow
                          key={si}
                          style={{
                            display: cards ? 'grid' : undefined,
                            gridTemplateColumns:
                              step === 0 && !mobile
                                ? 'repeat(2,minmax(0,1fr))'
                                : undefined,
                            gap: cards ? 'var(--sp-3)' : undefined,
                            minWidth: 0,
                            border: cards
                              ? '1px solid var(--border-subtle)'
                              : undefined,
                            borderBlockEnd: cards
                              ? undefined
                              : '1px solid var(--ink-100)',
                            borderRadius: cards ? 12 : undefined,
                            padding: cards ? 12 : undefined,
                            marginBlockEnd: cards ? 10 : undefined,
                            background:
                              segment.type === 'DEFAULT'
                                ? 'var(--ink-50)'
                                : 'white',
                          }}
                        >
                          <MatrixHeading
                            scope={cards ? undefined : 'row'}
                            style={{
                              display: cards ? undefined : 'block',
                              textAlign: 'start',
                              padding: cards ? undefined : '9px 10px',
                              overflowWrap: 'anywhere',
                              gridColumn: cards ? '1 / -1' : undefined,
                            }}
                          >
                            <span
                              style={{
                                display: 'block',
                                fontSize: 13,
                                lineHeight: '17px',
                                fontWeight: 800,
                                color: 'var(--brand-900)',
                              }}
                            >
                              {segment.type === 'DEFAULT'
                                ? t('pages.orderRouting.segments.default')
                                : segment.name}
                            </span>
                            <span
                              style={{
                                display: 'block',
                                marginBlockStart: 2,
                                fontSize: 10,
                                lineHeight: '13px',
                                fontWeight: 400,
                                color: 'var(--ink-500)',
                              }}
                            >
                              {segment.type === 'DEFAULT'
                                ? t('pages.orderRouting.segments.pinned')
                                : predicateSummary(segment, t)}
                            </span>
                          </MatrixHeading>
                          {OUTPUTS.map((output) => {
                            const cell = segment.cells.find(
                              (c) => c.outputKey === output,
                            )
                            const mode = cell?.valueMode ?? 'INHERIT'
                            const fee = output.endsWith('FEE_ITEM')
                            const category = OUTPUT_CATEGORY[output]
                            const value = fee
                              ? String(cell?.productId ?? '')
                              : output === 'REPORTING_TAG'
                                ? [cell?.externalId, cell?.secondaryExternalId]
                                    .filter(Boolean)
                                    .join('|')
                                : (cell?.externalId ?? '')
                            const options = fee
                              ? products.map((p) => ({
                                  value: String(p.id),
                                  label: `${p.name} · ${p.sku}`,
                                }))
                              : output === 'REPORTING_TAG'
                                ? (master.REPORTING_TAG ?? [])
                                    .filter((tag) => tag.active && !tag.staleAt)
                                    .flatMap((tag) =>
                                      (master.REPORTING_TAG_OPTION ?? [])
                                        .filter(
                                          (option) =>
                                            option.active &&
                                            !option.staleAt &&
                                            option.parentExternalId ===
                                              tag.externalId,
                                        )
                                        .map((option) => ({
                                          value: `${tag.externalId}|${option.externalId}`,
                                          label: `${tag.displayName} · ${option.displayName}`,
                                        })),
                                    )
                                : (master[category ?? ''] ?? [])
                                    .filter((x) => x.active && !x.staleAt)
                                    .map((x) => ({
                                      value: x.externalId,
                                      label: x.displayName,
                                    }))
                            return (
                              <MatrixCell
                                key={output}
                                style={{
                                  padding: cards ? 10 : 7,
                                  verticalAlign: 'middle',
                                  borderRadius: cards ? 10 : undefined,
                                  background: cards
                                    ? 'var(--ink-50)'
                                    : undefined,
                                  borderInlineStart:
                                    !cards && output === 'PAYMENT_FEE_ITEM'
                                      ? '1px solid var(--ink-100)'
                                      : undefined,
                                }}
                              >
                                {cards && (
                                  <label
                                    style={{ fontSize: 12, fontWeight: 700 }}
                                  >
                                    {t(`pages.orderRouting.outputs.${output}`)}
                                  </label>
                                )}
                                {fee ? (
                                  <>
                                    <select
                                      aria-label={`${t(`pages.orderRouting.outputs.${output}`)} ${t('pages.orderRouting.matrix.mode')}`}
                                      // The chip below carries the value, so the
                                      // mode stays visually secondary here.
                                      style={cellControlStyle(
                                        'INHERIT',
                                        segment.type === 'DEFAULT',
                                        !cards,
                                      )}
                                      disabled={locked}
                                      value={mode}
                                      onChange={(e) =>
                                        setCell(
                                          si,
                                          output,
                                          e.target.value as
                                            'INHERIT' | 'SET' | 'CLEAR',
                                        )
                                      }
                                    >
                                      <option value="INHERIT">
                                        {t('pages.orderRouting.matrix.inherit')}
                                      </option>
                                      <option value="SET">
                                        {t('pages.orderRouting.matrix.set')}
                                      </option>
                                      <option value="CLEAR">
                                        {t('pages.orderRouting.matrix.clear')}
                                      </option>
                                    </select>
                                    {mode === 'SET' &&
                                      (() => {
                                        // An id with no name behind it is not a
                                        // confirmed item: it stays a warning.
                                        const looked =
                                          cell?.productId != null
                                            ? feeProducts[cell.productId]
                                            : undefined
                                        const resolved =
                                          options.find(
                                            (option) => option.value === value,
                                          )?.label ||
                                          cell?.resolvedLabel ||
                                          (looked
                                            ? `${looked.name} · ${looked.sku}`
                                            : undefined)
                                        const label =
                                          resolved ||
                                          (value
                                            ? t(
                                                'pages.orderRouting.matrix.productId',
                                                { id: value },
                                              )
                                            : t(
                                                'pages.orderRouting.picker.title',
                                              ))
                                        return (
                                          <button
                                            type="button"
                                            disabled={locked}
                                            title={label}
                                            style={feeChipStyle(
                                              Boolean(resolved),
                                              !cards,
                                            )}
                                            onClick={() =>
                                              setProductPicker({
                                                segment: si,
                                                output,
                                                productId:
                                                  cell?.productId ?? null,
                                              })
                                            }
                                          >
                                            {label}
                                          </button>
                                        )
                                      })()}
                                  </>
                                ) : (
                                  <select
                                    aria-label={t(
                                      `pages.orderRouting.outputs.${output}`,
                                    )}
                                    // Columns are narrow; hovering reveals a
                                    // value the chip had to truncate.
                                    title={
                                      mode === 'SET'
                                        ? (options.find(
                                            (option) => option.value === value,
                                          )?.label ??
                                          cell?.resolvedLabel ??
                                          undefined)
                                        : undefined
                                    }
                                    style={cellControlStyle(
                                      mode,
                                      segment.type === 'DEFAULT',
                                      !cards,
                                    )}
                                    disabled={locked}
                                    value={
                                      mode === 'SET'
                                        ? value
                                        : mode === 'CLEAR'
                                          ? CLEAR_OPTION
                                          : INHERIT_OPTION
                                    }
                                    onChange={(e) => {
                                      const next = e.target.value
                                      if (next === INHERIT_OPTION)
                                        setCell(si, output, 'INHERIT')
                                      else if (next === CLEAR_OPTION)
                                        setCell(si, output, 'CLEAR')
                                      else setCell(si, output, 'SET', next)
                                    }}
                                  >
                                    <option value={INHERIT_OPTION}>
                                      {t('pages.orderRouting.matrix.inherit')}
                                    </option>
                                    {output !== 'CUSTOMER' &&
                                      output !== 'WAREHOUSE' && (
                                        <option value={CLEAR_OPTION}>
                                          {t('pages.orderRouting.matrix.clear')}
                                        </option>
                                      )}
                                    {mode === 'SET' && !value && (
                                      <option value="">
                                        {t('pages.orderRouting.matrix.choose')}
                                      </option>
                                    )}
                                    {value &&
                                      !options.some(
                                        (option) => option.value === value,
                                      ) && (
                                        <option value={value}>
                                          {cell?.resolvedLabel || value}
                                        </option>
                                      )}
                                    {options.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </MatrixCell>
                            )
                          })}
                        </MatrixRow>
                      ),
                  )}
                </MatrixBody>
              </Matrix>
            </div>
            {step === 0 && (
              <p
                style={{
                  display: 'flex',
                  gap: 'var(--sp-3)',
                  alignItems: 'center',
                  marginBlockEnd: 0,
                  padding: 12,
                  borderRadius: 12,
                  fontSize: 'var(--fs-13)',
                  background: defaultRouteComplete
                    ? 'var(--success-100)'
                    : 'var(--warning-100)',
                  color: defaultRouteComplete
                    ? 'var(--success-700)'
                    : 'var(--warning-700)',
                }}
              >
                {t(
                  defaultRouteComplete
                    ? 'pages.orderRouting.defaults.complete'
                    : 'pages.orderRouting.defaults.required',
                )}
              </p>
            )}
          </Card>
        )}
      </div>
      {step === 4 && (
        <Card padding={mobile ? 16 : 24}>
          <h3>{t('pages.orderRouting.review.title')}</h3>
          <p>
            {t('pages.orderRouting.review.summary', {
              channels: channelIds.length,
              actions: actions.filter((a) => a.statuses.length).length,
              segments: segments.length,
              cells: segments.reduce((n, s) => n + s.cells.length, 0),
            })}
          </p>
          <p style={{ color: 'var(--fg-muted)' }}>
            {t('pages.orderRouting.review.feeNotice')}
          </p>
          {!valid && (
            <p style={{ color: 'var(--danger)' }}>
              {t('pages.orderRouting.review.invalid')}
            </p>
          )}
          {!defaultRouteComplete && (
            <p
              style={{
                padding: 12,
                borderRadius: 12,
                fontSize: 'var(--fs-13)',
                background: 'var(--warning-100)',
                color: 'var(--warning-700)',
              }}
            >
              {t('pages.orderRouting.defaults.required')}
            </p>
          )}
          {Boolean(error) && (
            <p style={{ color: 'var(--danger)' }}>{errorText}</p>
          )}
          {saved && (
            <p style={{ color: 'var(--success)' }}>
              {t('pages.orderRouting.review.saved')}
            </p>
          )}
          <section
            style={{
              marginBlock: 16,
              paddingBlockStart: 16,
              borderBlockStart: '1px solid var(--border-subtle)',
            }}
          >
            <h3>{t('pages.orderRouting.preview.title')}</h3>
            <p style={{ color: 'var(--fg-muted)' }}>
              {t('pages.orderRouting.preview.help')}
            </p>
            <label
              style={{
                display: 'block',
                marginBlockEnd: 'var(--sp-2)',
                fontWeight: 700,
              }}
              htmlFor="routing-preview-order"
            >
              {t('pages.orderRouting.design.internalOrderId')}
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                id="routing-preview-order"
                aria-label={t('pages.orderRouting.preview.orderId')}
                type="number"
                min="1"
                style={{ ...inputStyle, maxWidth: 240 }}
                value={previewOrderId}
                onChange={(e) => {
                  setPreviewOrderId(e.target.value)
                  setPreview(null)
                  setPreviewError(false)
                }}
              />
              <Button
                kind="secondary"
                disabled={
                  !saved ||
                  previewLoading ||
                  !Number.isSafeInteger(Number(previewOrderId)) ||
                  Number(previewOrderId) <= 0
                }
                onClick={loadPreview}
              >
                {previewLoading
                  ? t('pages.orderRouting.preview.loading')
                  : t('pages.orderRouting.preview.run')}
              </Button>
            </div>
            {previewError && (
              <p role="alert" style={{ color: 'var(--danger)' }}>
                {t('pages.orderRouting.design.previewError')}
              </p>
            )}
            {preview && (
              <div
                aria-live="polite"
                style={{
                  marginBlockStart: 12,
                  padding: 12,
                  borderRadius: 10,
                  background: 'var(--ink-50)',
                }}
              >
                <strong>
                  {t('pages.orderRouting.preview.result', {
                    id: preview.orderId,
                  })}
                </strong>
                <p>
                  {t('pages.orderRouting.preview.action')}:{' '}
                  {preview.actionPlan.action
                    ? t(
                        `pages.orderRouting.actions.${preview.actionPlan.action}`,
                      )
                    : t('pages.orderRouting.preview.none')}
                </p>
                <p>
                  {t('pages.orderRouting.preview.segmentCount', {
                    count: preview.matchedSegmentIds?.length ?? 0,
                  })}
                </p>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: mobile
                      ? 'minmax(0,1fr)'
                      : 'minmax(220px,1fr) minmax(0,2fr)',
                    gap: 'var(--sp-4)',
                  }}
                >
                  <Card padding={16} style={{ minWidth: 0 }}>
                    <h3
                      style={{ marginBlockStart: 0, color: 'var(--brand-900)' }}
                    >
                      {t('pages.orderRouting.orderFacts')}
                    </h3>
                    {[
                      ['CHANNEL_PROVIDER', preview.facts.provider],
                      ['ORDER_STATUS', preview.facts.status],
                      ['FULFILLMENT_CHANNEL', preview.facts.fulfillmentChannel],
                      ['PAYMENT_METHOD_CODE', preview.facts.paymentMethodCode],
                      ['SHIPPING_METHOD', preview.facts.shippingMethod],
                    ].map(([field, value]) => (
                      <div
                        key={field}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 'var(--sp-3)',
                          paddingBlock: 'var(--sp-2)',
                          borderBlockEnd: '1px solid var(--border-subtle)',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        <span style={{ color: 'var(--fg-subtle)' }}>
                          {t(`pages.orderRouting.fields.${field}`)}
                        </span>
                        <strong>
                          {value || t('pages.orderRouting.preview.none')}
                        </strong>
                      </div>
                    ))}
                  </Card>
                  <Card padding={16} style={{ minWidth: 0 }}>
                    <h3
                      style={{ marginBlockStart: 0, color: 'var(--brand-900)' }}
                    >
                      {t('pages.orderRouting.resolvedMapping')}
                    </h3>
                    {(preview.outputResolutions ?? []).map((output) => (
                      <div
                        key={output.outputKey}
                        style={{
                          minWidth: 0,
                          padding: 12,
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 10,
                          overflowWrap: 'anywhere',
                          marginBlockEnd: 'var(--sp-2)',
                          display: 'grid',
                          gridTemplateColumns: mobile
                            ? 'minmax(0,1fr)'
                            : '120px minmax(0,1fr)',
                          columnGap: 'var(--sp-3)',
                        }}
                      >
                        <strong>
                          {t(`pages.orderRouting.outputs.${output.outputKey}`)}
                        </strong>
                        <div>
                          <Pill
                            tone={
                              output.sourceType === 'DEFAULT'
                                ? 'neutral'
                                : 'coral'
                            }
                          >
                            {t(
                              `pages.orderRouting.preview.resolutions.${output.resolution}`,
                            )}{' '}
                            ·{' '}
                            {output.sourceSegmentName ??
                              t(
                                output.sourceType === 'DEFAULT'
                                  ? 'pages.orderRouting.segments.default'
                                  : 'pages.orderRouting.preview.noSource',
                              )}
                          </Pill>
                        </div>
                        {output.resolution === 'SET' && (
                          <div
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 12,
                              gridColumn: '1 / -1',
                            }}
                          >
                            {[
                              output.label,
                              output.externalId,
                              output.secondaryExternalId,
                              output.productId,
                              output.mappedZohoItemId,
                            ]
                              .filter((value) => value != null)
                              .join(' · ')}
                          </div>
                        )}
                        {output.blockingErrors.map((error, index) => (
                          <div key={index} style={{ color: 'var(--danger)' }}>
                            {t(`pages.orderRouting.errors.${error.code}`, {
                              defaultValue: error.code,
                            })}
                          </div>
                        ))}
                      </div>
                    ))}
                  </Card>
                </div>
                {[...preview.blockingErrors, ...preview.warnings].map(
                  (issue, index) => (
                    <p key={index}>
                      {t(`pages.orderRouting.errors.${issue.code}`, {
                        defaultValue: issue.code,
                      })}
                      {issue.field ? ` · ${issue.field}` : ''}
                    </p>
                  ),
                )}
                {!!preview.blockingErrors.length && (
                  <Pill tone="danger">
                    {t('pages.orderRouting.preview.blockers', {
                      count: preview.blockingErrors.length,
                    })}
                  </Pill>
                )}
                {!!preview.warnings.length && (
                  <Pill tone="warning">
                    {t('pages.orderRouting.preview.warnings', {
                      count: preview.warnings.length,
                    })}
                  </Pill>
                )}
              </div>
            )}
          </section>
        </Card>
      )}
      <dialog
        ref={pickerDialog}
        aria-label={t('pages.orderRouting.picker.title')}
        onCancel={() => setProductPicker(null)}
        style={{
          width: mobile ? 'calc(100% - 28px)' : 700,
          maxWidth: 'calc(100% - 28px)',
          maxHeight: '85vh',
          // The dialog itself must not scroll, or the actions below the list
          // scroll out of reach; only the results list scrolls.
          overflow: 'hidden',
          padding: 0,
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--r-lg)',
          background: 'var(--bg-card)',
          color: 'var(--fg)',
          fontFamily: 'inherit',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        {productPicker && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '85vh',
            }}
          >
            <div
              style={{
                flexShrink: 0,
                padding: 'var(--sp-5)',
                paddingBlockEnd: 'var(--sp-4)',
              }}
            >
              <h3 style={{ marginBlockStart: 0, color: 'var(--brand-900)' }}>
                {t('pages.orderRouting.picker.title')}
              </h3>
              <p style={{ marginBlockEnd: 'var(--sp-4)' }}>
                {segments[productPicker.segment].name ||
                  t('pages.orderRouting.segments.default')}{' '}
                · {t(`pages.orderRouting.outputs.${productPicker.output}`)}
              </p>
              {pickerProduct && (
                <p
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                    gap: 'var(--sp-2)',
                    marginBlock: 0,
                    marginBlockEnd: 'var(--sp-4)',
                    padding: 'var(--sp-3)',
                    borderRadius: 'var(--r-md)',
                    background: 'var(--brand-100)',
                    color: 'var(--brand-900)',
                    fontSize: 'var(--fs-13)',
                  }}
                >
                  <strong>{t('pages.orderRouting.picker.selected')}</strong>
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {pickerProduct.name} · <code>{pickerProduct.sku}</code>
                  </span>
                </p>
              )}
              <label>
                {t('pages.orderRouting.matrix.productSearch')}
                <input
                  style={inputStyle}
                  value={productQuery}
                  onChange={(event) => setProductQuery(event.target.value)}
                />
              </label>
            </div>
            <div
              style={{
                display: 'grid',
                alignContent: 'start',
                gap: 'var(--sp-2)',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                paddingInline: 'var(--sp-5)',
                paddingBlockEnd: 'var(--sp-4)',
              }}
            >
              {productsLoading ? (
                <p>{t('pages.orderRouting.matrix.productsLoading')}</p>
              ) : productsError ? (
                <p role="alert">
                  {t('pages.orderRouting.matrix.productsError')}
                </p>
              ) : products.length === 0 ? (
                <p>{t('pages.orderRouting.picker.empty')}</p>
              ) : (
                products.map((product) => (
                  <label
                    key={product.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-3)',
                      padding: 'var(--sp-3)',
                      border: `1px solid var(${productPicker.productId === product.id ? '--brand-500' : '--border-subtle'})`,
                      borderRadius: 'var(--r-md)',
                      background:
                        productPicker.productId === product.id
                          ? 'var(--brand-100)'
                          : 'var(--bg-card)',
                    }}
                  >
                    <input
                      type="radio"
                      name="routing-fee-product"
                      checked={productPicker.productId === product.id}
                      onChange={() =>
                        setProductPicker({
                          ...productPicker,
                          productId: product.id,
                        })
                      }
                    />
                    <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                      {product.name}
                      <br />
                      <code>{product.sku}</code>
                    </span>
                  </label>
                ))
              )}
            </div>
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-4) var(--sp-5)',
                borderBlockStart: '1px solid var(--border-subtle)',
                background: 'var(--bg-card)',
              }}
            >
              <span
                style={{
                  flex: '1 1 260px',
                  minWidth: 0,
                  fontSize: 'var(--fs-12)',
                  color: 'var(--fg-subtle)',
                }}
              >
                {t('pages.orderRouting.picker.help')}
              </span>
              <span
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'end',
                  gap: 'var(--sp-2)',
                }}
              >
              <Button kind="secondary" onClick={() => setProductPicker(null)}>
                {t('pages.orderRouting.picker.cancel')}
              </Button>
              <Button
                disabled={productPicker.productId == null}
                onClick={() => {
                  setCell(
                    productPicker.segment,
                    productPicker.output,
                    'SET',
                    String(productPicker.productId),
                  )
                  setProductPicker(null)
                }}
              >
                {t('pages.orderRouting.picker.apply')}
              </Button>
              </span>
            </div>
          </div>
        )}
      </dialog>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBlockStart: 14,
        }}
      >
        <Button
          kind="secondary"
          disabled={step === 0}
          onClick={() => setStep((s) => s - 1)}
        >
          {t('pages.orderRouting.previous')}
        </Button>
        <Button
          kind="secondary"
          disabled={step === 4}
          onClick={() => setStep((s) => s + 1)}
        >
          {t('pages.orderRouting.next')}
        </Button>
      </div>
    </fieldset>
  )
}
